import React from 'react';
import '../styles/CurtainAnimation.css';

interface CurtainAnimationProps {
  isOpen: boolean;
}

const CurtainAnimation: React.FC<CurtainAnimationProps> = ({ isOpen }) => {
  // Calculate positions for a more organized star pattern
  const generateStarPositions = (count: number) => {
    return Array.from({ length: count }, (_, i) => {
      const section = Math.floor(i / (count / 5)); // Divide stars into 5 sections
      const sectionSize = 100 / 5;
      const baseDelay = section * 0.3; // Stagger delays by section
      
      return {
        delay: baseDelay + Math.random() * 0.5,
        top: (section * sectionSize) + Math.random() * sectionSize,
        left: Math.random() * 100,
        size: Math.random() * 3 + 1,
        rotation: Math.random() * 360
      };
    });
  };

  // Calculate positions for a more organized shooting star pattern
  const generateShootingStars = (count: number) => {
    return Array.from({ length: count }, (_, i) => ({
      delay: i * 1.5 + Math.random(), // Evenly staggered delays
      top: 20 + Math.random() * 30, // Keep in upper portion of screen
      left: 10 + (i * (80 / count)) // Evenly distribute across width
    }));
  };

  // Calculate organized decorative element positions
  const generateDecorativeElements = (count: number, side: 'left' | 'right') => {
    const positions = [];
    const rows = 5;
    const itemsPerRow = count / rows;
    
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < itemsPerRow; col++) {
        positions.push({
          top: (row * (100 / rows)) + Math.random() * (100 / rows),
          left: (col * (100 / itemsPerRow)) + Math.random() * (100 / itemsPerRow),
          rotation: Math.random() * 360,
          delay: (row * 0.2) + (col * 0.1) + Math.random() * 0.3
        });
      }
    }
    
    return positions;
  };

  const starPositions = generateStarPositions(80);
  const shootingStarPositions = generateShootingStars(5);
  const leftDecorativeElements = generateDecorativeElements(15, 'left');
  const rightDecorativeElements = generateDecorativeElements(15, 'right');

  return (
    <div className={`curtain-container ${isOpen ? 'open' : ''}`}>
      <div className="stars-container">
        {starPositions.map((pos, i) => (
          <div key={`star-${i}`} className="star" style={{
            '--delay': `${pos.delay}s`,
            '--top': `${pos.top}%`,
            '--left': `${pos.left}%`,
            '--size': `${pos.size}px`,
            '--rotation': `${pos.rotation}deg`
          } as React.CSSProperties}></div>
        ))}
      </div>
      <div className="shooting-stars">
        {shootingStarPositions.map((pos, i) => (
          <div key={`shooting-star-${i}`} className="shooting-star" style={{
            '--delay': `${pos.delay}s`,
            '--top': `${pos.top}%`,
            '--left': `${pos.left}%`
          } as React.CSSProperties}></div>
        ))}
      </div>
      <div className="curtain left-curtain">
        <div className="curtain-texture">
          {leftDecorativeElements.map((pos, i) => (
            <div key={`flower-left-${i}`} className="flower" style={{
              '--top': `${pos.top}%`,
              '--left': `${pos.left}%`,
              '--rotation': `${pos.rotation}deg`,
              '--delay': `${pos.delay}s`
            } as React.CSSProperties}></div>
          ))}
        </div>
        <div className="curtain-fold fold1"></div>
        <div className="curtain-fold fold2"></div>
        <div className="curtain-fold fold3"></div>
      </div>
      <div className="curtain right-curtain">
        <div className="curtain-texture">
          {rightDecorativeElements.map((pos, i) => (
            <div key={`flower-right-${i}`} className="flower" style={{
              '--top': `${pos.top}%`,
              '--left': `${pos.left}%`,
              '--rotation': `${pos.rotation}deg`,
              '--delay': `${pos.delay}s`
            } as React.CSSProperties}></div>
          ))}
        </div>
        <div className="curtain-fold fold1"></div>
        <div className="curtain-fold fold2"></div>
        <div className="curtain-fold fold3"></div>
      </div>
      <div className="stage-light left"></div>
      <div className="stage-light right"></div>
      <div className="welcome-text">
        <div className="welcome-animation">
          <h1>
            <span className="welcome-word">Welcome</span>
            <span className="to-word">to</span>
            <div className="tripgo-text">
              <span className="trip-word">Trip</span>
              <span className="go-word">Go</span>
            </div>
          </h1>
          <p className="journey-text">Your Journey Begins Here</p>
        </div>
        <div className="sparkles">
          {generateStarPositions(20).map((pos, i) => (
            <div key={`sparkle-${i}`} className="sparkle" style={{
              '--delay': `${pos.delay}s`,
              '--top': `${pos.top}%`,
              '--left': `${pos.left}%`
            } as React.CSSProperties}></div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CurtainAnimation;